import { max, sum } from 'lodash'
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { Control, FormState, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  BemCorrectionFactorsData,
  BuildingEnergyModelFragment,
  ConsumptionEnergyType,
} from '../../../generated/graphql'
import { HourSeriesResult } from '../../../building-energy-model/hour-series-calculator'
import {
  energyCapacityPerAreaUnit,
  energyPerYearUnit,
  formatEnergy,
  formatPower,
  hourUnit,
  powerPerAreaUnit,
  powerUnit,
} from '../../../util/formatters'
import {
  calcSerieSum,
  FactorFormValues,
  mapInputToFactorFormValues,
} from './summary-util'
import { ControlledTextField } from '../../form/ControlledTextField'
import { validators } from '../../../validation/validators'
import { EmptyTableContainer } from './EmptyTableContainer'

export type SummaryTableRowData = {
  hourSeriesInfo: HourSeriesResult
  sumValue: number
  maxValue: number
}

export const initTableData = (
  hourSeries: HourSeriesResult[],
): SummaryTableRowData[] =>
  hourSeries.map(
    (serie) =>
      ({
        hourSeriesInfo: serie,
        sumValue: calcSerieSum(serie.hourSerie),
        maxValue: max(serie.hourSerie),
      } as SummaryTableRowData),
  )

export const formatSumValuePerArea = (value: number): string =>
  value.toLocaleString('fi-FI', {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
  })

export const formatEffectValue = (value: number): string =>
  value.toLocaleString('fi-FI', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })

export const isValidAndDirty = async (
  fieldName: string | undefined,
  formState: FormState<FactorFormValues>,
  control: Control<FactorFormValues>,
): Promise<boolean> => {
  const { isDirty } = formState
  const isValid = fieldName ? await control.trigger(fieldName) : true
  return isDirty && isValid
}

export const useStyles = makeStyles(() => ({
  tableValueCell: {
    textAlign: 'center',
  },
  tableRow: {
    height: '48px',
  },
  highlightFont: {
    fontWeight: 'bold',
  },
  doubleBorderBottom: {
    borderBottomStyle: 'solid',
    borderBottomWidth: 'medium',
  },
  doubleBorderTop: {
    borderTopStyle: 'solid',
    borderTopWidth: 'medium',
  },
}))

export type SummaryTableProps = {
  model: BuildingEnergyModelFragment
  hourSeries: HourSeriesResult[]
  sumSerie: number[]
  correctionFactors: BemCorrectionFactorsData[]
  originalHourSeriesSums: Partial<Record<ConsumptionEnergyType, number>>
  onUpdateFactorData: (
    energyType: ConsumptionEnergyType,
    fieldName: keyof Pick<
      BemCorrectionFactorsData,
      'correctionFactor' | 'energyFactor'
    >,
    factor: string,
  ) => boolean
}

export function BemSummaryTable({
  model,
  hourSeries,
  sumSerie,
  correctionFactors,
  originalHourSeriesSums,
  onUpdateFactorData,
}: SummaryTableProps): React.ReactElement {
  const [tableData, setTableData] = useState<SummaryTableRowData[]>([])
  const [sumSerieMax, setSumSerieMax] = useState(0)

  const { control, errors, formState, reset } = useForm<FactorFormValues>({
    defaultValues: mapInputToFactorFormValues(correctionFactors),
  })

  const { t } = useTranslation()
  const classes = useStyles()

  useEffect(() => {
    setTableData(initTableData(hourSeries))
    setSumSerieMax(max(sumSerie) as number)
  }, [hourSeries, sumSerie, correctionFactors]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    reset(mapInputToFactorFormValues(correctionFactors))
    void isValidAndDirty(undefined, formState, control)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const updateFactorData = async (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>,
    energyType: ConsumptionEnergyType,
    fieldName: keyof Pick<
      BemCorrectionFactorsData,
      'correctionFactor' | 'energyFactor'
    >,
  ) => {
    if (await isValidAndDirty(event.target.name, formState, control)) {
      onUpdateFactorData(energyType, fieldName, event.target.value)
      reset(control.getValues())
    }
  }

  const getSumValueSum = () => sum(tableData.map((d) => d.sumValue))

  const getOriginalSumValueSum = () =>
    sum(
      tableData.map(
        (d) => originalHourSeriesSums[d.hourSeriesInfo.consumptionEnergyType],
      ),
    )

  const getEffectValueString = (
    sumValue: number,
    originalSumValue: number | undefined,
  ): string => {
    if (!originalSumValue || originalSumValue === 0) return '1'

    return formatEffectValue(sumValue / originalSumValue)
  }

  return (
    <>
      {tableData.length > 0 ? (
        <form>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>{energyPerYearUnit}</TableCell>
                  <TableCell>{energyCapacityPerAreaUnit}</TableCell>
                  <TableCell>{powerUnit}</TableCell>
                  <TableCell>{powerPerAreaUnit}</TableCell>
                  <TableCell>
                    {`${t(
                      'buildingEnergyModels.summary.maxValueUsageTime',
                    )}, ${hourUnit}`}
                  </TableCell>
                  <TableCell>
                    {t('buildingEnergyModels.summary.correctionFactor')}
                  </TableCell>
                  <TableCell>
                    {t('buildingEnergyModels.summary.energyCorrection')}
                  </TableCell>
                  <TableCell>
                    {t('buildingEnergyModels.summary.effect')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((data, index) => (
                  <TableRow
                    className={classes.tableRow}
                    key={index.toString()}
                    hover
                  >
                    <TableCell width="20%">
                      {data.hourSeriesInfo.hourSerieName}
                    </TableCell>
                    <TableCell>
                      {formatEnergy(data.sumValue, { omitUnit: true })}
                    </TableCell>
                    <TableCell>
                      {formatSumValuePerArea(
                        data.sumValue / (1000 * model.area),
                      )}
                    </TableCell>
                    <TableCell>
                      {formatPower(data.maxValue, { omitUnit: true })}
                    </TableCell>
                    <TableCell>
                      {formatSumValuePerArea(data.maxValue / model.area)}
                    </TableCell>
                    <TableCell>
                      {data.maxValue > 0
                        ? formatPower((data.sumValue / data.maxValue) * 1000, {
                            omitUnit: true,
                          })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <ControlledTextField
                        control={control}
                        name={`factorData.${index}.correctionFactor`}
                        error={errors.factorData?.at(index)?.correctionFactor}
                        rules={{
                          ...validators.required,
                          ...validators.copModifier,
                        }}
                        size="small"
                        onBlur={async (event) =>
                          updateFactorData(
                            event,
                            data.hourSeriesInfo.consumptionEnergyType,
                            'correctionFactor',
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <ControlledTextField
                        control={control}
                        name={`factorData.${index}.energyFactor`}
                        error={errors.factorData?.at(index)?.energyFactor}
                        rules={{
                          ...validators.required,
                          ...validators.decimal,
                        }}
                        size="small"
                        onBlur={async (event) =>
                          updateFactorData(
                            event,
                            data.hourSeriesInfo.consumptionEnergyType,
                            'energyFactor',
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {getEffectValueString(
                        data.sumValue,
                        originalHourSeriesSums[
                          data.hourSeriesInfo.consumptionEnergyType
                        ],
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className={classes.tableRow}>
                  <TableCell className={classes.highlightFont}>
                    Yhteensä
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {formatEnergy(getSumValueSum(), { omitUnit: true })}
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {formatSumValuePerArea(
                      getSumValueSum() / (1000 * model.area),
                    )}
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {formatPower(sumSerieMax, { omitUnit: true })}
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {formatSumValuePerArea(sumSerieMax / model.area)}
                  </TableCell>
                  <TableCell className={classes.highlightFont}>
                    {sumSerieMax > 0
                      ? formatPower((getSumValueSum() / sumSerieMax) * 1000, {
                          omitUnit: true,
                        })
                      : '-'}
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell className={`${classes.highlightFont}`}>
                    {getEffectValueString(
                      getSumValueSum(),
                      getOriginalSumValueSum(),
                    )}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </form>
      ) : (
        <EmptyTableContainer
          text={t('buildingEnergyModels.summary.noTableData')}
        />
      )}
    </>
  )
}
